import dayjs from 'dayjs';
import { message } from 'antd';
import { API_URL } from 'env';
import { mixLogOut } from '../../services/mixin';
import { callCenterApi, setCallLogSink, type CallLog, type LogCallInput } from './callCenterStore';
import { CARESOFT_ENABLED, isLive } from './caresoft';

/**
 * Đồng bộ nhật ký cuộc gọi lên máy chủ (API call-logs, BE chốt 28/10/2026).
 * Store localStorage vẫn là nơi giao diện đọc; mỗi nhật ký mới được đẩy lên server và gắn serverId để sửa ghi chú, nghe ghi âm.
 * Chỉ chạy khi bật REACT_APP_CARESOFT=on — tắt thì giữ chế độ mô phỏng thuần.
 */
export const CALL_LOG_API = CARESOFT_ENABLED;

const DT = 'YYYY-MM-DD HH:mm:ss';

/** Lý do thất bại FE (FAIL_REASONS) → mã BE: 1 Không nghe · 2 Từ chối · 3 Sai số · 4 Không tồn tại · 5 Không liên lạc được */
const FAIL_REASON_TO_BE: Record<number, number> = { 1: 3, 2: 5, 3: 2, 4: 2, 5: 5 };

interface ApiResult<T> { ok: boolean; status: number; data?: T; msg?: string }

async function api<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`${API_URL}/api/v1/be${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      },
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 401) mixLogOut();
    return { ok: res.ok && json?.status !== false, status: res.status, data: json?.data, msg: json?.msg };
  } catch {
    return { ok: false, status: 0, msg: 'Không kết nối được máy chủ' };
  }
}

const requestId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`);

async function pushCallLog(call: CallLog, input: LogCallInput) {
  // Cuộc gọi mô phỏng (CareSoft lỗi, đang chạy dự phòng) không đẩy lên máy chủ — chỉ cuộc gọi qua tổng đài thật hoặc ghi tay
  if (!call.manual && !call.caresoftCallId && !isLive()) return;
  const failReason = call.failReason ? FAIL_REASON_TO_BE[call.failReason] : undefined;
  const r = await api<{ id: number }>('/call-logs', {
    method: 'POST',
    body: JSON.stringify({
      client_request_id: requestId(),
      phone: call.phone,
      direction: call.direction,
      outcome: call.outcome,
      duration: call.duration,
      started_at: dayjs(call.startedAt).format(DT),
      ...(call.note ? { note: call.note } : {}),
      ...(failReason ? { fail_reason: failReason } : {}),
      ...(input.callbackAt ? { callback_at: dayjs(input.callbackAt).format(DT) } : {}),
      ...(input.newCustomerName?.trim() ? { new_customer_name: input.newCustomerName.trim() } : {}),
      manual: !!call.manual,
      ...(call.caresoftCallId ? { caresoft_call_id: call.caresoftCallId } : {}),
    }),
  });
  if (r.ok && r.data?.id) callCenterApi.setCallServerId(call.id, r.data.id);
  else message.warning(`Chưa lưu được nhật ký cuộc gọi lên máy chủ${r.msg ? `: ${r.msg}` : ''}`);
}

async function pushNote(call: CallLog) {
  if (!call.serverId) return;
  const r = await api(`/call-logs/${call.serverId}`, { method: 'PATCH', body: JSON.stringify({ note: call.note || '' }) });
  if (!r.ok) message.warning(`Chưa lưu được nội dung lên máy chủ${r.msg ? `: ${r.msg}` : ''}`);
}

export interface Recording { url: string; download_url?: string }

/** Link nghe ghi âm. 404 = CareSoft chưa có file (vừa cúp máy) → cho thử lại sau */
export async function fetchRecording(serverId: number): Promise<{ rec?: Recording; status: number; msg?: string }> {
  const r = await api<Recording>(`/call-logs/${serverId}/recording`);
  return r.ok && r.data?.url ? { rec: r.data, status: r.status } : { status: r.status, msg: r.msg };
}

if (CALL_LOG_API) {
  setCallLogSink({
    logged: (call, input) => { pushCallLog(call, input); },
    noteUpdated: call => { pushNote(call); },
  });
}
